import { useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { FileText, UserIcon } from 'lucide-react';
import { CardTabIcon, CardTabItem, CardTabs, CardTabText } from 'components/ui/CardTabs';

/** Base tab type that both QuoteTabDto and BorrowerQuoteTabDto satisfy */
interface BaseTabDto {
  label: string;
  borrowerName?: string;
  clientPrice?: number;
  product?: string | number;
}

interface ContractualizationTabsProps<T extends BaseTabDto> {
  tabs: T[];
  activeTab?: string;
  onTabClick: (tabId: string, tab: T) => void;
  /** Function to get the unique identifier for a tab */
  getTabId: (tab: T) => string;
  /** Function to extract display name from tab label */
  getTabName?: (tab: T) => string;
}

/** Price of a quote as shown on the tabs; also used by the quote chips. */
export const formatPrice = (price: number) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(price);

/**
 * Generic tabs component for contractualization pages
 * Used by ContractSimulations and BorrowerTabs, drawn by the shared CardTabs
 */
export function ContractualizationTabs<T extends BaseTabDto>({
  tabs,
  activeTab,
  onTabClick,
  getTabId,
  getTabName
}: ContractualizationTabsProps<T>) {
  const defaultGetTabName = useCallback((tab: T) => {
    const splits = tab.label.split('-');
    return splits[3] || tab.label;
  }, []);

  const getName = getTabName || defaultGetTabName;

  const items = useMemo<CardTabItem[]>(
    () => tabs.map(tab => {
      const tabId = getTabId(tab);

      return {
        id: tabId,
        isActive: tabId === activeTab,
        onSelect: () => onTabClick(tabId, tab),
        render: (isActive: boolean) => (
          <>
            <CardTabIcon isActive={isActive}>
              {tab.borrowerName ? <UserIcon className="w-[19px] h-[19px]" /> : <FileText className="w-[19px] h-[19px]" />}
            </CardTabIcon>

            <CardTabText isActive={isActive} eyebrow={tab.product || undefined}>
              {tab.borrowerName || `Devis N°${getName(tab)}`}
            </CardTabText>

            {tab.clientPrice !== undefined && tab.clientPrice !== null && (
              <span
                className={clsx(
                  'ml-auto flex-none px-2 py-1 rounded-lg text-xs font-bold tabular-nums whitespace-nowrap transition-colors',
                  isActive ? 'bg-primary/20 text-primary' : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                )}
              >
                {formatPrice(tab.clientPrice)}
              </span>
            )}
          </>
        )
      };
    }),
    [tabs, activeTab, onTabClick, getTabId, getName]
  );

  return <CardTabs items={items} />;
}
